import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { BadgeDollarSign, Coins, Smartphone } from "lucide-react";
import { db } from "./firebase";

interface SubscriptionRecord {
  status?: string;
  plan?: string;
  productId?: string;
  trialEndsAt?: { toDate?: () => Date };
  complimentaryEndsAt?: { toDate?: () => Date };
  currentPeriodEndsAt?: { toDate?: () => Date };
  willRenew?: boolean;
}

interface CreditRecord {
  allowance?: number;
  used?: number;
  reserved?: number;
  available?: number;
  periodKey?: string;
}

export function SubscriptionStatusView({ userId }: { userId: string }) {
  const [subscription, setSubscription] = useState<SubscriptionRecord>({});
  const [credits, setCredits] = useState<CreditRecord>({});
  const [error, setError] = useState("");
  useEffect(() => onSnapshot(doc(db, "userAccess", userId), (snapshot) => setSubscription(snapshot.data() ?? {}), (reason) => setError(reason.message)), [userId]);
  useEffect(() => onSnapshot(doc(db, "creditAccounts", userId), (snapshot) => setCredits(snapshot.data() ?? {}), (reason) => setError(reason.message)), [userId]);
  const expiry = subscription.trialEndsAt || subscription.complimentaryEndsAt || subscription.currentPeriodEndsAt;
  const allowance = Number(credits.allowance ?? 0);
  const available = Number(credits.available ?? 0);
  const used = Number(credits.used ?? 0);
  const percentage = allowance > 0 ? Math.min(100, Math.round((used / allowance) * 100)) : 0;

  return <section className="subscription-status">
    <header><div><h2>Plan &amp; Credits</h2><p>Your E2B entitlement is shared across iOS and the web app.</p></div><span><BadgeDollarSign size={15} />{subscription.plan || "E2B"}</span></header>
    {error && <div className="notice error">{error}</div>}
    <div className="subscription-grid">
      <article><small>Status</small><strong>{formatStatus(subscription.status)}</strong><span>{expiry?.toDate?.() ? `${subscription.willRenew === false ? "Available until" : "Current period ends"} ${expiry.toDate!().toLocaleDateString()}` : "No expiry recorded"}</span></article>
      <article><small>AI credits</small><strong>{available === -1 ? "Unlimited" : available.toLocaleString()}</strong><span>{allowance === -1 ? "Owner allowance" : `${used.toLocaleString()} of ${allowance.toLocaleString()} used · ${credits.periodKey || "current period"}`}</span>{allowance > 0 && <div className="credit-meter"><i style={{ width: `${percentage}%` }} /></div>}</article>
      <article><small>Subscription</small><strong>US$14.99 monthly</strong><span>or US$149.99 annually · new accounts receive a 30-day E2B trial · 1,500 monthly AI credits</span></article>
    </div>
    <div className="ios-purchase-note"><Smartphone size={17} /><div><strong>Subscribe and manage billing in the iOS app</strong><span>Apple handles purchases, renewals, and cancellations. Your Firebase account unlocks this web workspace automatically.</span></div></div>
  </section>;
}

function formatStatus(value?: string) {
  return ({
    owner: "Owner",
    approved: "Legacy tester",
    trialing: "30-day trial",
    active: "E2B Pro",
    grace_period: "Billing grace period",
    complimentary: "Complimentary access",
    read_only: "Read-only",
    expired: "Read-only",
    suspended: "Suspended",
  } as Record<string, string>)[value || ""] || "Starting account";
}
